import { useMemo, useState } from "react";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Grid2, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import cn from "classnames";
import { useTranslate } from "../../../../../hooks/useTranslate";
import SummaryBox from "../../../../../components/SummaryBox";
import { isVersionUK } from "../../../../../services/versionService";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import QuestionInfo from "../../../../../assets/images/icons/question-circle.svg?react";
import "./CheckoutForm.css";

const categoryPriority = {
    TuitionFees: 0,
    TuitionFeesAddon: 1,
};

const paymentElementOptions = {
    type: "accordion",
    radios: true,
    defaultCollapsed: false,
    paymentMethodOrder: isVersionUK ? ["bacs_debit", "customer_balance", "card", "pay_by_bank"] : ["us_bank_account", "card", "customer_balance"],
};

const CheckoutForm = (props) => {
    const {
        invoice, selectedType, isOnRollingContract, isInvoicePay, instalment,
    } = props;
    const { t } = useTranslate();

    const stripe = useStripe();
    const elements = useElements();

    const [isPaying, setIsPaying] = useState(false);
    const [open, setOpen] = useState(false);
    const [method, setMethod] = useState();
    const [errorMessage, setErrorMessage] = useState("");

    const onPay = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) return;

        setIsPaying(true);
        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.href}?payment-complete`,
            },
        });

        if (error) setErrorMessage(error.message);
        setIsPaying(false);
    };

    const {
        total, totalDiscount, totalExcludingTax, totalTax, items, startingBalance, currency = "GBP",
    } = invoice;
    const itemsSorted = useMemo(() => (items || [])
        .map((item) => ({ ...item, title: item.description.replace(/\[.*?\]/g, "").trim() }))
        .toSorted((a, b) => {
            const aPriority = categoryPriority[a.category] ?? 2;
            const bPriority = categoryPriority[b.category] ?? 2;
            return aPriority - bPriority;
        }), [items]);

    const discountPercentage = useMemo(() => {
        if (!totalDiscount) return 0;

        const totalToCalculate = items.filter((item) => item.category !== "TuitionFees").reduce((total, item) => total + item.amount, 0);

        return (totalDiscount / (totalExcludingTax - totalToCalculate + totalDiscount)) * 100;
    }, [items, totalDiscount, totalExcludingTax]);

    return (
        <Grid2 container spacing={2} className="billing-grid">
            <Grid2 size={{ xs: 12, md: 6, lg: 7 }}>
                <SummaryBox isFormBox>
                    {itemsSorted.map((itemLine) => (
                        <>
                            <div className="billing-grid__row mb-4">
                                <div>
                                    <Typography variant="h3" component="div" className={cn("text-primary", itemLine.category === "TuitionFees" ? "billing-total-title" : "billing-title")}>
                                        {itemLine.title}
                                    </Typography>
                                    {itemLine.category === "TuitionFees" && (
                                        <Typography variant="subtitle2" component="span" className="billing-grid__subtitle">
                                            {selectedType === "Monthly"
                                                ? t("tuitionBilling.payMonthly")
                                                : t(isOnRollingContract ? "tuitionBilling.payHalfTermly" : "tuitionBilling.payInFull")}
                                        </Typography>
                                    )}
                                </div>
                                <span className="billing-grid__price">
                                    <strong>{formatCurrency(currency).format(itemLine.amount)}</strong>
                                </span>
                            </div>
                            {(itemLine.discounts?.length > 0 || itemLine.promos?.length > 0) ? (
                                <div className="billing-discounts-block">
                                    {itemLine.discounts.map((discount) => (
                                        <div key={discount.discountId} className="billing-discounts-block__code text-primary">
                                            <span className="billing-discount-badge">{t("tuitionBilling.discount")}</span>
                                            {discount.name}
                                            <span className="billing-discounts-block__price">
                                                {`-${formatCurrency(currency, 2).format(discount.amount)}`}
                                            </span>
                                        </div>
                                    ))}
                                    {itemLine.promos.map((promo) => (
                                        <div key={promo.promoCodeId} className="billing-discounts-block__code text-primary">
                                            <span className="billing-discount-badge">{t("tuitionBilling.discount")}</span>
                                            {promo.name}
                                            <span className="billing-discounts-block__price">
                                                {`-${formatCurrency(currency, 2).format(promo.amount)}`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </>
                    ))}
                    {/* {totalDiscount > 0 && ( */}
                    {/*    <div className="billing-grid__row billing-grid__row-discount"> */}
                    {/*        <div className="flex align-center"> */}
                    {/*            <span className="info-icon">i</span> */}
                    {/*            <span className="info-text"> */}
                    {/*                {t("tuitionBilling.discountApplied")} */}
                    {/*            </span> */}
                    {/*        </div> */}
                    {/*    </div> */}
                    {/* )} */}
                    <div className="divider mb-4" />
                    {/* {totalDiscount > 0 && ( */}
                    {/*    <div className="billing-grid__row mb-2"> */}
                    {/*        <div className="billing-badge">{t("tuitionBilling.discounted")}</div> */}
                    {/*    </div> */}
                    {/* )} */}
                    {startingBalance ? (
                        <div className="billing-grid__row mb-4">
                            <div className="flex align-center">
                                <Typography variant="h3" component="div" className="text-primary billing-title">
                                    {t("tuitionBilling.appliedBalance")}
                                </Typography>
                                <Tooltip
                                    onClose={() => setOpen(false)}
                                    open={open}
                                    arrow
                                    disableHoverListener
                                    className="tooltip"
                                    title={t("tuitionBilling.appliedBalanceHelp")}
                                >
                                    <IconButton onClick={() => setOpen(true)}>
                                        <QuestionInfo className="billing-grid__question" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                            <span className="billing-grid__price">
                                <strong>{formatCurrency(currency).format(startingBalance)}</strong>
                            </span>
                        </div>
                    ) : null}
                    {/* <div className="billing-grid__row">
                        <Typography variant="h4" component="div" className="text-primary billing-title">
                            {t("tuitionBilling.subtotal")}
                        </Typography>
                        <Typography variant="h5" component="div" className="text-primary billing-grid__price">
                            {formatCurrency(currency).format(totalExcludingTax + (startingBalance || 0))}
                        </Typography>
                    </div>
                    {totalTax > 0 && (
                        <div className="billing-grid__row mt-2">
                            <Typography variant="h4" component="div" className="text-primary billing-title">
                                {t("tuitionBilling.tax")}
                            </Typography>
                            <Typography variant="h5" component="div" className="text-primary billing-grid__price">
                                {formatCurrency(currency).format(totalTax)}
                            </Typography>
                        </div>
                    )} */}
                    <div className="billing-grid__row mt-4">
                        <Typography variant="h4" component="div" className="text-primary billing-total-title">
                            {t("tuitionBilling.totalPayNow")}
                        </Typography>
                        <span className="billing-grid__price">
                            <strong>
                                {formatCurrency(currency).format(total + (startingBalance || 0))}
                            </strong>
                        </span>
                    </div>
                    {(selectedType === "Monthly" && instalment) ? (
                        <div className="billing-followed-recurring">
                            <Typography variant="body1" component="div" className="text-primary">
                                {t("tuitionBilling.followedMonthlyRecurring")}
                            </Typography>
                            <span className="billing-followed-recurring__price text-primary">
                                <strong>{formatCurrency(currency).format(instalment)}</strong>
                            </span>
                        </div>
                    ) : null}
                    {selectedType === "Monthly" ? (
                        <Typography variant="body1" component="div" className="mt-8 text-primary">
                            <b>{t("tuitionBilling.totalMonthlyNoteTitle")}</b>
                            {t("tuitionBilling.totalMonthlyNote")}
                        </Typography>
                    ) : null}
                    {/* {isVersionUK && ( */}
                    {/*    <div className="billing-grid__row"> */}
                    {/*        <span /> */}
                    {/*        <Typography variant="subtitle2" component="span" className="billing-grid__subtitle"> */}
                    {/*            {t("tuitionBilling.includingVAT")} */}
                    {/*        </Typography> */}
                    {/*    </div> */}
                    {/* )} */}
                </SummaryBox>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6, lg: 5 }}>
                <SummaryBox isFormBox>
                    {isInvoicePay && (
                        <Typography variant="h2" component="div" className="text-primary billing-title">
                            {t("payInvoice.selectPaymentMethod")}
                        </Typography>
                    )}
                    <form onSubmit={onPay} className={cn("payment-form-card", isInvoicePay ? "mt-4" : "")}>
                        <PaymentElement
                            id="payment-element"
                            options={paymentElementOptions}
                            onChange={(val) => {
                                setMethod(val?.collapsed ? undefined : val?.value?.type);
                            }}
                        />
                        <Button
                            type="submit"
                            disabled={!method}
                            loading={isPaying}
                            className="pay-now-button"
                        >
                            {t("tuitionBilling.payNow")}
                        </Button>
                        {errorMessage && <div className="error-message">{errorMessage}</div>}
                    </form>
                </SummaryBox>
            </Grid2>
        </Grid2>
    );
};

export default CheckoutForm;
